/**
 * Leases past due by at least the threshold below. Same shape and same
 * `BalanceTable` as the rent roll — this is the rent roll with a floor on
 * `maxDaysLate`, which is exactly what the service does.
 */

import { useState } from 'react';
import { useList } from '@refinedev/core';
import { Link } from 'react-router';
import type { SessionBalance } from 'shared';
import { BalanceTable } from '../../../components/BalanceTable.js';

export function Delinquency() {
  const [minDaysLate, setMinDaysLate] = useState('1');
  const [asOf, setAsOf] = useState('');

  const { data, isLoading, error } = useList<SessionBalance>({
    resource: 'ledger/delinquency',
    pagination: { mode: 'off' },
    filters: [
      { field: 'minDaysLate', operator: 'eq', value: minDaysLate || undefined },
      { field: 'asOf', operator: 'eq', value: asOf || undefined },
    ],
  });

  return (
    <section>
      <h1>Delinquency</h1>
      <p className="muted">
        Leases carrying a balance at least this many days past due. Collections calls start here.
        For a 0–30/31–60/61–90/90+ breakdown of the same balance, see{' '}
        <Link to="/staff/reports?tab=ar-aging">AR aging</Link>.
      </p>

      <div className="row">
        <label className="row">
          <span className="muted">At least</span>
          <input
            className="field__input"
            type="number"
            min="0"
            style={{ width: '6rem' }}
            value={minDaysLate}
            onChange={(event) => setMinDaysLate(event.target.value)}
          />
          <span className="muted">days late</span>
        </label>
        <input
          className="field__input"
          type="date"
          value={asOf}
          onChange={(event) => setAsOf(event.target.value)}
        />
      </div>

      {isLoading ? <p className="muted">Loading…</p> : null}
      {error ? (
        <p className="alert alert--error">
          {error instanceof Error ? error.message : 'Could not load the delinquency report.'}
        </p>
      ) : null}
      {!isLoading && !error ? (
        <BalanceTable balances={data?.data ?? []} emptyMessage="Nothing this delinquent." />
      ) : null}
    </section>
  );
}
